/**
 * Property detail hero — photo + sub-line (Active · Listed N days ago · MLS#) +
 * listed/offer prices + address + spec chips. Matches the find-properties.html
 * mockup layout: 280px photo on the left, info column on the right with the
 * Listed/Your Offer price pair only rendered when the user's purchase differs.
 *
 * The photo carousel from the mockup is a Phase-4 polish item — single image
 * for now (no MLS multi-image data available).
 */
import { Bath, Bed, Building2, CalendarDays, Ruler } from "lucide-react";

import { cn } from "@/lib/utils";
import { fmtMoney, parseMoney } from "@/lib/calculators";
import type { PropertySnapshot } from "./detail-helpers";

interface DetailHeroProps {
  snapshot: PropertySnapshot;
  purchase: number;
  /** Optional year-built (just for display). */
  yearBuilt?: number | null;
  /** Optional MLS number string. */
  mlsNumber?: string | null;
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((word) => {
      if (word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, ""))) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function DetailHero({
  snapshot,
  purchase,
  yearBuilt,
  mlsNumber,
}: DetailHeroProps) {
  const listed = parseMoney(snapshot.price ?? 0);
  const showOffer = purchase > 0 && listed > 0 && purchase !== listed;
  const delta = showOffer ? purchase - listed : 0;
  const deltaPct = showOffer && listed > 0 ? (delta / listed) * 100 : 0;
  const isUnder = delta < 0;
  const formattedListed =
    typeof snapshot.price === "number"
      ? fmtMoney(snapshot.price)
      : (snapshot.price ?? "—");

  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-[280px_1fr] md:gap-5">
      {/* Photo */}
      <div
        className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-border bg-cover bg-center"
        style={
          snapshot.imgUrl
            ? { backgroundImage: `url('${snapshot.imgUrl}')` }
            : undefined
        }
        aria-hidden
      >
        <span className="absolute left-3 top-3 rounded bg-success px-2 py-0.5 text-xs font-bold text-white">
          Active
        </span>
      </div>

      {/* Info column */}
      <div className="flex min-w-0 flex-col justify-center">
        {/* Sub-line: Active · Listed N days ago · MLS# */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-success">Active</span>
          {snapshot.days && (
            <>
              <span className="h-1 w-1 rounded-full bg-muted-foreground" />
              <span>Listed {snapshot.days} ago</span>
            </>
          )}
          {mlsNumber && (
            <>
              <span className="h-1 w-1 rounded-full bg-muted-foreground" />
              <span>MLS #{mlsNumber}</span>
            </>
          )}
        </div>

        {/* Listed price + (when offer differs) Your Offer */}
        <div className="mt-2 flex flex-wrap items-start gap-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Listed Price
            </div>
            <div className="text-2xl font-extrabold leading-tight">
              {formattedListed}
            </div>
          </div>
          {showOffer && (
            <>
              <div className="self-stretch w-px bg-border" aria-hidden />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Your Offer
                </div>
                <div
                  className={cn(
                    "text-2xl font-extrabold leading-tight",
                    isUnder ? "text-success" : "text-destructive",
                  )}
                >
                  {fmtMoney(purchase)}
                </div>
                <div
                  className={cn(
                    "mt-1 text-[11px] font-bold",
                    isUnder ? "text-success" : "text-destructive",
                  )}
                >
                  {isUnder ? "−" : "+"}
                  {fmtMoney(Math.abs(delta))} ({isUnder ? "−" : "+"}
                  {Math.abs(deltaPct).toFixed(1)}% vs listed)
                </div>
              </div>
            </>
          )}
        </div>

        {/* Address */}
        {snapshot.addr1 && (
          <div className="mt-3 text-lg font-bold leading-tight tracking-wide">
            {titleCase(snapshot.addr1)}
          </div>
        )}
        {snapshot.addr2 && (
          <div className="mt-0.5 text-sm text-muted-foreground">
            {titleCase(snapshot.addr2)}
          </div>
        )}

        {/* Spec chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {snapshot.bd != null && (
            <SpecChip Icon={Bed}>{snapshot.bd} beds</SpecChip>
          )}
          {snapshot.ba != null && (
            <SpecChip Icon={Bath}>{snapshot.ba} baths</SpecChip>
          )}
          {snapshot.sqft != null && (
            <SpecChip Icon={Ruler}>{snapshot.sqft} sqft</SpecChip>
          )}
          <SpecChip Icon={Building2}>Single Family</SpecChip>
          {yearBuilt && (
            <SpecChip Icon={CalendarDays}>Built {yearBuilt}</SpecChip>
          )}
        </div>
      </div>
    </section>
  );
}

interface SpecChipProps {
  Icon: typeof Bed;
  children: React.ReactNode;
}

function SpecChip({ Icon, children }: SpecChipProps) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/50 px-2 py-1 text-[11px] leading-tight">
      <Icon className="h-3 w-3 text-muted-foreground" aria-hidden />
      {children}
    </span>
  );
}
